import type { CSSProperties, ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { describeError, isApiError } from '@/api/errors';
import { cx } from '@/lib/cx';
import { Button } from './controls';

/**
 * Empty state: one helpful line and at most one action, e.g.
 * <EmptyState title="No drafts waiting." action={<Button size="sm">Start conversation</Button>} />
 * `centered` for a whole empty page or pane; default sits inline in a list.
 */
export function EmptyState({
  title,
  action,
  centered,
  compact,
  className,
}: {
  title: ReactNode;
  action?: ReactNode;
  centered?: boolean;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={cx('state', centered && 'state-centered', compact && 'state-compact', className)}>
      <p>{title}</p>
      {action}
    </div>
  );
}

function BlockState({
  title,
  children,
  meta,
  action,
  className,
  role,
}: {
  title: ReactNode;
  children?: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
  className?: string;
  role?: 'status' | 'alert';
}) {
  return (
    <div className={cx('state-block', className)} role={role}>
      <p className="state-title">{title}</p>
      {children ? <div className="state-body">{children}</div> : null}
      {meta ? <p className="state-meta">{meta}</p> : null}
      {action}
    </div>
  );
}

/** The membership lacks an explicit grant; the request is not sent. */
export function ScopeRequiredState({ what }: { what: string }) {
  return <EmptyState title={`No access to ${what.toLowerCase()}. Access grants: Settings › Members.`} />;
}

/** API returned 501: the operation is in the contract but not built. Never shown as a network error. */
export function NotBuiltState({
  operationId,
  plannedPhase,
  requestId,
  what,
}: {
  operationId?: string;
  plannedPhase?: string;
  requestId?: string;
  what?: string;
}) {
  return (
    <BlockState
      title="Not available yet"
      role="status"
      meta={
        operationId || plannedPhase || requestId ? (
          <>
            {operationId ? <code>{operationId}</code> : null}
            {plannedPhase ? <> planned for {plannedPhase}</> : null}
            {requestId ? <> request <code>{requestId}</code></> : null}
          </>
        ) : undefined
      }
    >
      {what ? `${what}: not` : 'Not'} built on the server yet. No simulated data.
    </BlockState>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (isApiError(error) && error.notImplemented) {
    return <NotBuiltState operationId={error.operationId} plannedPhase={error.plannedPhase} requestId={error.requestId} />;
  }
  const { title, body } = describeError(error);
  const requestId = isApiError(error) ? error.requestId : undefined;
  const retryable = !isApiError(error) || error.status === 0 || error.status >= 500 || error.status === 429;
  return (
    <BlockState
      className="state-error"
      title={title}
      role="alert"
      meta={requestId ? <>Request <code>{requestId}</code></> : undefined}
      action={
        onRetry && retryable ? (
          <Button size="sm" icon="refresh" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    >
      {body}
    </BlockState>
  );
}

export function Skeleton({ width = '100%', height = 12, style }: { width?: number | string; height?: number; style?: CSSProperties }) {
  return <span className="skeleton" style={{ width, height, ...style }} aria-hidden />;
}

export function LoadingState({ label = 'Loading', lines = 3, compact }: { label?: string; lines?: number; compact?: boolean }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className="stack"
      style={{ padding: compact ? 'var(--space-2) 0' : 'var(--space-4) 0' }}
    >
      <span className="visually-hidden">{label}</span>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={`${90 - ((i * 17) % 40)}%`} />
      ))}
    </div>
  );
}

/**
 * Render a query's loading / error / not built states, or its data. Keeps
 * previously loaded data visible during background refresh instead of flashing empty.
 */
export function QueryState<T>({
  query,
  children,
  loading,
  compact,
}: {
  query: Pick<UseQueryResult<T>, 'data' | 'error' | 'isPending' | 'isError' | 'refetch'>;
  children: (data: T) => ReactNode;
  loading?: ReactNode;
  compact?: boolean;
}) {
  if (query.isPending) return <>{loading ?? <LoadingState compact={compact} />}</>;
  if (query.isError && query.data === undefined) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return <>{children(query.data as T)}</>;
}
